import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Plus, Search, Wrench, ChevronRight, Upload, FileText,
  User, Package, Building2, Clock, MoreVertical, MessageSquare,
  Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const statusLabels = {
  open: 'Ouvert',
  diagnosed: 'Diagnostiqué',
  waiting_parts: 'Attente pièces',
  in_repair: 'En réparation',
  ready: 'Prêt à rendre',
  closed: 'Clôturé'
};

const statusColors = {
  open: 'bg-[#2A1515] text-[#E74C3C] border-[#E74C3C]/20',
  diagnosed: 'bg-[#152A35] text-[#3498DB] border-[#3498DB]/20',
  waiting_parts: 'bg-[#2A2010] text-[#F39C12] border-[#F39C12]/20',
  in_repair: 'bg-[#2A2010] text-[#F39C12] border-[#F39C12]/20',
  ready: 'bg-[#142915] text-[#00E01A] border-[#00E01A]/20',
  closed: 'bg-[#2C3338] text-[#8C8F94] border-[#8C8F94]/20'
};

const WorkflowSteps = ({ currentStatus, onStatusChange }) => {
  const steps = ['open', 'diagnosed', 'waiting_parts', 'in_repair', 'ready', 'closed'];
  const currentIndex = steps.indexOf(currentStatus);

  return (
    <div className="flex flex-wrap gap-2">
      {steps.map((step, idx) => (
        <button
          key={step}
          onClick={() => onStatusChange(step)}
          disabled={idx > currentIndex + 1}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            step === currentStatus
              ? 'bg-[#00E01A] text-black'
              : idx < currentIndex
              ? 'bg-[#142915] text-[#00E01A] border border-[#00E01A]/30'
              : idx === currentIndex + 1
              ? 'bg-[#1D2327] text-white border border-[#2C3338] hover:border-[#00E01A]/50 cursor-pointer'
              : 'bg-[#1D2327] text-[#52555A] border border-[#2C3338] cursor-not-allowed'
          }`}
          data-testid={`workflow-step-${step}`}
        >
          {statusLabels[step]}
        </button>
      ))}
    </div>
  );
};

const SAVForm = ({ clients, equipment, suppliers, franchises, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    client_id: '',
    equipment_id: '',
    serial_number: '',
    supplier_id: '',
    description: '',
    franchise_id: franchises[0]?.id || ''
  });
  const [filteredEquipment, setFilteredEquipment] = useState([]);

  useEffect(() => {
    if (formData.client_id) {
      const clientEquipment = equipment.filter(e => e.client_id === formData.client_id);
      setFilteredEquipment(clientEquipment);
    } else {
      setFilteredEquipment([]);
    }
  }, [formData.client_id, equipment]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Client *</Label>
          <Select
            value={formData.client_id}
            onValueChange={(value) => setFormData({ ...formData, client_id: value, equipment_id: '' })}
          >
            <SelectTrigger data-testid="sav-client-select">
              <SelectValue placeholder="Sélectionner..." />
            </SelectTrigger>
            <SelectContent className="max-h-[200px]">
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Équipement</Label>
          <Select
            value={formData.equipment_id}
            onValueChange={(value) => setFormData({ ...formData, equipment_id: value })}
            disabled={!formData.client_id}
          >
            <SelectTrigger data-testid="sav-equipment-select">
              <SelectValue placeholder={formData.client_id ? "Sélectionner..." : "Choisir un client d'abord"} />
            </SelectTrigger>
            <SelectContent>
              {filteredEquipment.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.product_name} {e.serial_number && `(${e.serial_number})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>N° de série (si pas d'équipement)</Label>
          <Input
            value={formData.serial_number}
            onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
            placeholder="Optionnel"
            className="font-mono"
            disabled={!!formData.equipment_id}
            data-testid="sav-serial-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Fournisseur</Label>
          <Select
            value={formData.supplier_id}
            onValueChange={(value) => setFormData({ ...formData, supplier_id: value })}
          >
            <SelectTrigger data-testid="sav-supplier-select">
              <SelectValue placeholder="Sélectionner..." />
            </SelectTrigger>
            <SelectContent>
              {suppliers.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Franchise *</Label>
          <Select
            value={formData.franchise_id}
            onValueChange={(value) => setFormData({ ...formData, franchise_id: value })}
          >
            <SelectTrigger data-testid="sav-franchise-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {franchises.map((f) => (
                <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Description du problème *</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Décrivez le problème rencontré..."
          rows={4}
          required
          data-testid="sav-description-input"
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-[#2C3338]">
        <Button type="button" variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <Button type="submit" className="bg-[#00E01A] hover:bg-[#00B815] text-black">
          Créer le ticket
        </Button>
      </div>
    </form>
  );
};

const PDFUploadDialog = ({ suppliers, onExtract }) => {
  const [file, setFile] = useState(null);
  const [supplierId, setSupplierId] = useState('');
  const [loading, setLoading] = useState(false);

  const handleUpload = async () => {
    if (!file) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const result = await onExtract(formData, supplierId);
      toast.success('PDF analysé avec succès');
      return result;
    } catch (error) {
      console.error('Failed to extract PDF:', error);
      toast.error('Erreur lors de l\'analyse du PDF');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Fournisseur (optionnel)</Label>
        <Select value={supplierId} onValueChange={setSupplierId}>
          <SelectTrigger>
            <SelectValue placeholder="Sélectionner pour prompt personnalisé..." />
          </SelectTrigger>
          <SelectContent>
            {suppliers.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
          file ? 'border-[#00E01A] bg-[#00E01A]/5' : 'border-[#2C3338] hover:border-[#00E01A]/50'
        }`}
      >
        <input
          type="file"
          accept=".pdf"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="hidden"
          id="pdf-upload"
          data-testid="pdf-upload-input"
        />
        <label htmlFor="pdf-upload" className="cursor-pointer">
          {file ? (
            <div className="flex items-center justify-center gap-2 text-[#00E01A]">
              <FileText size={24} />
              <span>{file.name}</span>
            </div>
          ) : (
            <>
              <Upload size={32} className="mx-auto text-[#8C8F94] mb-2" />
              <p className="text-[#8C8F94]">Cliquez ou glissez un PDF de récépissé fournisseur</p>
            </>
          )}
        </label>
      </div>

      <Button
        onClick={handleUpload}
        disabled={!file || loading}
        className="w-full bg-[#00E01A] hover:bg-[#00B815] text-black"
        data-testid="extract-pdf-btn"
      >
        {loading ? (
          <>
            <Loader2 size={16} className="animate-spin mr-2" />
            Analyse en cours...
          </>
        ) : (
          <>
            <FileText size={16} className="mr-2" />
            Extraire les données
          </>
        )}
      </Button>
    </div>
  );
};

const SAVTicketCard = ({ ticket, onStatusChange }) => (
  <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-4 hover:border-[#00E01A]/30 transition-all">
    <div className="flex items-start justify-between mb-3">
      <div>
        <Link 
          to={`/sav/${ticket.id}`}
          className="text-[#00E01A] font-mono text-sm hover:underline"
          data-testid={`sav-ticket-link-${ticket.id}`}
        >
          {ticket.ticket_number}
        </Link>
        <p className="text-white font-medium mt-1">{ticket.client_name}</p>
      </div>
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${statusColors[ticket.status]}`}>
        {statusLabels[ticket.status]}
      </span>
    </div>

    <p className="text-[#8C8F94] text-sm mb-3 line-clamp-2">{ticket.description}</p>

    <div className="flex flex-wrap gap-2 mb-3">
      {ticket.product_name && (
        <span className="inline-flex items-center gap-1 text-xs text-[#8C8F94]">
          <Package size={12} />
          {ticket.product_name}
        </span>
      )}
      {ticket.supplier_name && (
        <span className="inline-flex items-center gap-1 text-xs text-[#8C8F94]">
          <Building2 size={12} />
          {ticket.supplier_name}
        </span>
      )}
    </div>

    <div className="flex items-center justify-between pt-3 border-t border-[#2C3338]">
      <span className="text-[#52555A] text-xs">
        {new Date(ticket.created_at).toLocaleDateString('fr-FR')}
      </span>
      <div className="flex items-center gap-2">
        {ticket.assigned_technician_name && (
          <span className="inline-flex items-center gap-1 text-xs text-[#8C8F94]">
            <User size={12} />
            {ticket.assigned_technician_name}
          </span>
        )}
        <Link
          to={`/sav/${ticket.id}`}
          className="text-[#8C8F94] hover:text-[#00E01A] transition-colors"
        >
          <ChevronRight size={16} />
        </Link>
      </div>
    </div>
  </div>
);

const SAV = () => {
  const { api, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [clients, setClients] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const clientIdParam = searchParams.get('client_id');
      const ticketUrl = clientIdParam ? `/sav?client_id=${clientIdParam}` : '/sav';
      
      const [ticketsRes, clientsRes, equipmentRes, suppliersRes, franchisesRes] = await Promise.all([
        api.get(ticketUrl),
        api.get('/clients'),
        api.get('/equipment'),
        api.get('/suppliers'),
        api.get('/franchises')
      ]);
      setTickets(ticketsRes.data);
      setClients(clientsRes.data);
      setEquipment(equipmentRes.data);
      setSuppliers(suppliersRes.data);
      setFranchises(franchisesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [api, searchParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async (data) => {
    try {
      await api.post('/sav', data);
      toast.success('Ticket SAV créé');
      fetchData();
      setDialogOpen(false);
    } catch (error) {
      console.error('Failed to create ticket:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const handlePDFExtract = async (formData, supplierId) => {
    const url = supplierId ? `/sav/extract-pdf?supplier_id=${supplierId}` : '/sav/extract-pdf';
    const response = await api.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  };

  const filteredTickets = tickets.filter(t => {
    const matchesSearch = 
      t.ticket_number?.toLowerCase().includes(search.toLowerCase()) ||
      t.client_name?.toLowerCase().includes(search.toLowerCase()) ||
      t.description?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
    const supplierParam = searchParams.get('supplier_id');
    const matchesSupplier = !supplierParam || t.supplier_id === supplierParam;
    return matchesSearch && matchesStatus && matchesSupplier;
  });

  // Group tickets by status for kanban-like view
  const openTickets = filteredTickets.filter(t => t.status === 'open');
  const inProgressTickets = filteredTickets.filter(t => ['diagnosed', 'waiting_parts', 'in_repair'].includes(t.status));
  const readyTickets = filteredTickets.filter(t => t.status === 'ready');
  const closedTickets = filteredTickets.filter(t => t.status === 'closed');

  if (loading) {
    return (
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-4 animate-pulse">
              <div className="h-4 w-24 bg-[#2C3338] rounded mb-3"></div>
              <div className="h-20 bg-[#2C3338] rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">
            SAV & Maintenance
          </h1>
          <p className="text-[#8C8F94] mt-1">
            {tickets.length} ticket{tickets.length > 1 ? 's' : ''} • {openTickets.length} ouvert{openTickets.length > 1 ? 's' : ''}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Dialog open={pdfDialogOpen} onOpenChange={setPdfDialogOpen}>
            <DialogTrigger asChild>
              <button
                className="flex items-center gap-2 bg-[#1D2327] hover:bg-[#2C3338] text-white font-medium px-4 py-2.5 rounded-full border border-[#2C3338] transition-all"
                data-testid="upload-pdf-btn"
              >
                <Upload size={18} />
                <span className="hidden md:inline">Importer PDF</span>
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold font-['Outfit']">
                  Importer un récépissé fournisseur
                </DialogTitle>
              </DialogHeader>
              <PDFUploadDialog suppliers={suppliers} onExtract={handlePDFExtract} />
            </DialogContent>
          </Dialog>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <button
                className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-4 py-2.5 rounded-full transition-all shadow-[0_0_15px_rgba(0,224,26,0.3)]"
                data-testid="add-sav-btn"
              >
                <Plus size={18} />
                <span className="hidden md:inline">Nouveau ticket</span>
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold font-['Outfit']">
                  Nouveau ticket SAV
                </DialogTitle>
              </DialogHeader>
              <SAVForm
                clients={clients}
                equipment={equipment}
                suppliers={suppliers}
                franchises={franchises}
                onSave={handleSave}
                onClose={() => setDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C8F94]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un ticket..."
            className="w-full bg-[#1D2327] border border-[#2C3338] rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-[#52555A] focus:outline-none focus:border-[#00E01A] transition-colors"
            data-testid="sav-search-input"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-48 bg-[#1D2327] border-[#2C3338]">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent className="bg-[#1D2327] border-[#2C3338]">
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="open">Ouvert</SelectItem>
            <SelectItem value="diagnosed">Diagnostiqué</SelectItem>
            <SelectItem value="waiting_parts">Attente pièces</SelectItem>
            <SelectItem value="in_repair">En réparation</SelectItem>
            <SelectItem value="ready">Prêt à rendre</SelectItem>
            <SelectItem value="closed">Clôturé</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tickets Kanban View */}
      {filteredTickets.length === 0 ? (
        <div className="text-center py-12">
          <Wrench size={48} className="mx-auto text-[#8C8F94] mb-4" />
          <p className="text-[#8C8F94]">Aucun ticket trouvé</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Open */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-3 h-3 rounded-full bg-[#E74C3C]"></span>
              <h3 className="text-white font-semibold">Ouverts ({openTickets.length})</h3>
            </div>
            <div className="space-y-3">
              {openTickets.map(ticket => (
                <SAVTicketCard key={ticket.id} ticket={ticket} />
              ))}
            </div>
          </div>

          {/* In Progress */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-3 h-3 rounded-full bg-[#F39C12]"></span>
              <h3 className="text-white font-semibold">En cours ({inProgressTickets.length})</h3>
            </div>
            <div className="space-y-3">
              {inProgressTickets.map(ticket => (
                <SAVTicketCard key={ticket.id} ticket={ticket} />
              ))}
            </div>
          </div>

          {/* Ready */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-3 h-3 rounded-full bg-[#00E01A]"></span>
              <h3 className="text-white font-semibold">Prêts ({readyTickets.length})</h3>
            </div>
            <div className="space-y-3">
              {readyTickets.map(ticket => (
                <SAVTicketCard key={ticket.id} ticket={ticket} />
              ))}
            </div>
          </div>

          {/* Closed */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-3 h-3 rounded-full bg-[#8C8F94]"></span>
              <h3 className="text-white font-semibold">Clôturés ({closedTickets.length})</h3>
            </div>
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {closedTickets.slice(0, 5).map(ticket => (
                <SAVTicketCard key={ticket.id} ticket={ticket} />
              ))}
              {closedTickets.length > 5 && (
                <p className="text-[#8C8F94] text-sm text-center">
                  + {closedTickets.length - 5} autres
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SAV;
